import { useCallback, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
  GroupType,
  ListCostOffsetCodeDocument,
  SortDirection,
  type CostOffsetCode as GqlCostOffsetCode,
  type ListCostOffsetCodeQuery,
  type ListCostOffsetCodeQueryVariables,
} from 'graphql-query-contracts';

const DEFAULT_PER_PAGE = 50;
const DEFAULT_START = 0;

export type CostOffsetCodeSortKey = 'code';

export interface CostOffsetCodesPagination {
  start?: number;
  perPage?: number;
}

export interface CostOffsetCodesSortBy {
  key: CostOffsetCodeSortKey;
  direction: SortDirection;
}

export interface UseCostOffsetCodesParams {
  groupId: string;
  groupType: GroupType;
  pagination?: CostOffsetCodesPagination;
  sortBy?: CostOffsetCodesSortBy;
}

export interface CostOffsetCodesPageInfo {
  start: number;
  total: number;
  perPage: number;
}

export interface CostOffsetCodeListItem {
  id: string;
  code: string;
  description?: string;
  groupId: string;
  groupType: GroupType;
}

export interface UseCostOffsetCodesResult {
  fetchCostOffsetCodes: (
    overrides?: Partial<UseCostOffsetCodesParams>
  ) => Promise<void>;
  costOffsetCodes: CostOffsetCodeListItem[];
  pageInfo: CostOffsetCodesPageInfo;
  loading: boolean;
  error: Error | undefined;
  called: boolean;
}

function buildVariables(
  params: UseCostOffsetCodesParams
): ListCostOffsetCodeQueryVariables {
  return {
    listCostOffsetCodeRequest: {
      groupId: params.groupId,
      groupType: params.groupType,
      pagination: {
        start: params.pagination?.start ?? DEFAULT_START,
        perPage: params.pagination?.perPage ?? DEFAULT_PER_PAGE,
      },
      sortBy: params.sortBy
        ? {
            key: params.sortBy.key,
            dir: params.sortBy.direction,
          }
        : undefined,
    },
  };
}

function mapGqlCostOffsetCodeToListItem(
  gql: GqlCostOffsetCode
): CostOffsetCodeListItem {
  return {
    id: String(gql.id),
    code: gql.code,
    description: gql.description ?? undefined,
    groupId: String(gql.groupId),
    groupType: gql.groupType,
  };
}

export function useCostOffsetCodes(): UseCostOffsetCodesResult {
  const [executeQuery, { data, loading, error, called }] = useLazyQuery<
    ListCostOffsetCodeQuery,
    ListCostOffsetCodeQueryVariables
  >(ListCostOffsetCodeDocument);

  const fetchCostOffsetCodes = useCallback(
    async (overrides?: Partial<UseCostOffsetCodesParams>): Promise<void> => {
      if (!overrides) {
        return;
      }

      if (!overrides.groupId || overrides.groupType === undefined) {
        return;
      }

      await executeQuery({
        variables: buildVariables({
          groupId: overrides.groupId,
          groupType: overrides.groupType,
          pagination: overrides.pagination,
          sortBy: overrides.sortBy,
        }),
      });
    },
    [executeQuery]
  );

  const costOffsetCodes = useMemo(() => {
    const list = data?.accounts?.listCostOffsetCode?.costOffsetCodes;

    if (!list) {
      return [];
    }

    return list.map(mapGqlCostOffsetCodeToListItem);
  }, [data]);

  const pageInfo = useMemo<CostOffsetCodesPageInfo>(() => {
    const info = data?.accounts?.listCostOffsetCode?.pageInfo;

    return {
      start: info?.start ?? DEFAULT_START,
      total: info?.total ?? 0,
      perPage: info?.perPage ?? DEFAULT_PER_PAGE,
    };
  }, [data]);

  return {
    fetchCostOffsetCodes,
    costOffsetCodes,
    pageInfo,
    loading,
    error: error ?? undefined,
    called,
  };
}
